import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PageBody } from '@/components/AppShell'
import { Chessboard } from '@/components/Chessboard'
import { OVERLINE } from '@/components/EvidenceCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { adminApi, type FeedbackRow } from '@/lib/api'
import { cn } from '@/lib/utils'

const FILTERS = [
  { value: 'wszystkie', label: 'wszystkie' },
  { value: 'sporne', label: 'sporne' },
  { value: 'negatywne', label: 'nietrafne' },
  { value: 'bez_odpowiedzi', label: 'bez odpowiedzi' },
  { value: 'oznaczone', label: 'do rewizji' },
] as const

/**
 * The collected feedback, board by board.
 *
 * Reading order is the point: rows where reviewers disagreed come first under
 * the "sporne" filter, because one of those readings is wrong and that is
 * where an hour of the owner's time is worth most.
 */
export function FeedbackPage() {
  const datasets = useQuery({ queryKey: ['admin-datasets'], queryFn: adminApi.datasets })
  const [chosen, setChosen] = useState<string | null>(null)
  const [filter, setFilter] = useState<string>('wszystkie')
  const [selected, setSelected] = useState<string | null>(null)

  // The newest report is the one being worked on, so it is the default rather
  // than an empty picker waiting for a click.
  const datasetId = chosen ?? datasets.data?.[0]?.id ?? ''

  const rows = useQuery({
    queryKey: ['admin-feedback', datasetId, filter],
    queryFn: () => adminApi.feedback(datasetId, filter),
    enabled: Boolean(datasetId),
  })

  const current = rows.data?.find((row) => row.board.id === selected) ?? rows.data?.[0]

  /**
   * The whole round in one file: every piece of evidence with the reviewers'
   * verdicts and their own words, the closing survey, and the grades block the
   * report generator reads, nested under `review`.
   */
  const download = async () => {
    const payload = await adminApi.exportFeedback(datasetId)
    const stem = datasetId.replace(/[^a-z0-9-]/gi, '_')
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }),
    )
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `${stem}_feedback.json`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  return (
    <PageBody className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={datasetId}
          onValueChange={(value) => {
            setChosen(value)
            setSelected(null)
          }}
        >
          <SelectTrigger className="w-64">
            <SelectValue placeholder="wybierz zbiór" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {(datasets.data ?? []).map((dataset) => (
                <SelectItem key={dataset.id} value={dataset.id}>
                  {dataset.label} · {dataset.slug} v{dataset.version}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>

        <ToggleGroup
          type="single"
          variant="outline"
          value={filter}
          onValueChange={(value) => value && setFilter(value)}
        >
          {FILTERS.map((item) => (
            <ToggleGroupItem key={item.value} value={item.value}>
              {item.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>

        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={() => void download()} disabled={!datasetId}>
          Pobierz JSON
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(340px,440px)]">
        <Card className="gap-0 overflow-hidden py-0">
          <CardContent className="max-h-[72vh] overflow-y-auto p-0">
            {rows.data?.length === 0 ? (
              <Empty>
                <EmptyHeader>
                  <EmptyTitle>Nic nie pasuje do tego filtra</EmptyTitle>
                  <EmptyDescription>Spróbuj „wszystkie”.</EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-card">
                  <TableRow>
                    <TableHead className="w-20 pl-4">Dowód</TableHead>
                    <TableHead>Ruchy</TableHead>
                    <TableHead className="w-40 pr-4 text-right">Recenzenci</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(rows.data ?? []).map((row, position) => {
                    const opensAxis = rows.data?.[position - 1]?.axisKey !== row.axisKey
                    const active = current?.board.id === row.board.id
                    return (
                      <Fragment key={row.board.id}>
                        {/* The axis is written once above its evidence rather
                            than truncated into every row, which is what made
                            sixty-two rows read as one grey block. */}
                        {opensAxis ? (
                          <TableRow className="hover:bg-transparent">
                            <TableCell
                              colSpan={3}
                              className="bg-muted py-2 pl-4 text-xs font-semibold"
                            >
                              Oś {row.axisOrdinal}
                              <span className="ml-2 font-normal text-muted-foreground">
                                {row.axisTitle}
                              </span>
                            </TableCell>
                          </TableRow>
                        ) : null}
                        <TableRow
                          onClick={() => setSelected(row.board.id)}
                          className={cn('cursor-pointer', active && 'bg-accent/60')}
                        >
                          <TableCell
                            className={cn(
                              'border-l-2 py-2 pl-4 tabular-nums',
                              active ? 'border-primary font-medium' : 'border-transparent',
                            )}
                          >
                            {row.board.ordinal}
                            {row.board.review.flagged ? (
                              <span className="ml-1 text-amber" title="do rewizji">
                                ?
                              </span>
                            ) : null}
                          </TableCell>
                          <TableCell className="py-2 font-mono text-xs">
                            {row.board.played.san}{' '}
                            <span className="text-faint">/</span>{' '}
                            {row.board.alternative.san}
                          </TableCell>
                          <TableCell className="py-2 pr-4">
                            <Distribution row={row} />
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {current ? <Detail key={current.board.id} row={current} /> : null}
      </div>
    </PageBody>
  )
}

/**
 * How the verdicts fell, at a glance.
 *
 * A dash where nobody answered: most of a fresh round is unanswered, and
 * sixty rows repeating "brak odpowiedzi" buries the handful that were.
 */
function Distribution({ row }: { row: FeedbackRow }) {
  const { trafny, czesciowo, nietrafny, answers } = row.counts
  if (answers === 0) {
    return <span className="block text-right text-faint">—</span>
  }
  const parts = [
    { count: trafny, className: 'bg-green' },
    { count: czesciowo, className: 'bg-amber' },
    { count: nietrafny, className: 'bg-clay' },
  ].filter((part) => part.count > 0)
  return (
    <span className="flex items-center justify-end gap-2">
      <span className="flex gap-0.5">
        {parts.map((part) =>
          Array.from({ length: part.count }, (_, index) => (
            <span
              key={`${part.className}-${index}`}
              className={cn('size-2 rounded-full', part.className)}
            />
          )),
        )}
      </span>
      <span className="text-xs tabular-nums text-muted-foreground">{answers}</span>
    </span>
  )
}

/** How one wording fared: verdict counts, and how often it was preferred. */
function VariantScore({ score }: { score: FeedbackRow['variants'][number] }) {
  const answers = score.trafny + score.czesciowo + score.nietrafny
  if (answers === 0 && score.preferred === 0) {
    return <span className="font-normal text-faint normal-case">bez ocen</span>
  }
  return (
    <span className="flex items-center gap-1.5 font-normal normal-case">
      {score.generator ? (
        <Badge variant="outline">{score.generator}</Badge>
      ) : null}
      {score.trafny > 0 ? <Badge tone="green">{score.trafny} trafny</Badge> : null}
      {score.czesciowo > 0 ? (
        <Badge tone="amber">{score.czesciowo} częściowo</Badge>
      ) : null}
      {score.nietrafny > 0 ? (
        <Badge tone="clay">{score.nietrafny} nietrafny</Badge>
      ) : null}
      {score.preferred > 0 ? (
        <Badge tone="navy">wybrana {score.preferred}×</Badge>
      ) : null}
    </span>
  )
}

function preferredWording(row: FeedbackRow, better: string): string {
  if (better === 'both') return 'obie wersje'
  if (better === 'neither') return 'żadna wersja'
  const variant = row.variants.find((item) => item.variant === better)
  return variant?.generator ?? `wariant ${better.toUpperCase()}`
}

const POLE_PLACEMENT_BADGES = {
  tak: { label: 'właściwy biegun', tone: 'green' },
  nie: { label: 'drugi biegun', tone: 'amber' },
  inna_os: { label: 'inna oś', tone: 'clay' },
  nie_pasuje: { label: 'nie pasuje do osi', tone: 'clay' },
} as const

function PolePlacementBadge({
  value,
}: {
  value: FeedbackRow['responses'][number]['answer']['polePlacement']
}) {
  if (!value) return null
  const badge = POLE_PLACEMENT_BADGES[value]
  return <Badge tone={badge.tone}>{badge.label}</Badge>
}

function Detail({ row }: { row: FeedbackRow }) {
  return (
    <Card className="h-fit">
      <CardHeader>
        <CardTitle className="text-sm">
          Oś {row.axisOrdinal} · dowód {row.board.ordinal} ·{' '}
          <span className="font-mono">{row.board.moveLabel}</span>
        </CardTitle>
        <CardDescription>{row.axisTitle}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Chessboard
          fen={row.board.fen}
          playedUci={row.board.played.uci}
          alternativeUci={row.board.alternative.uci}
          className="mx-auto max-w-[320px]"
        />

        {row.board.commentaries.map((wording, index) => (
          <div
            key={wording.variant}
            className="flex flex-col gap-2 text-sm leading-relaxed text-ink-soft"
          >
            {row.board.commentaries.length > 1 ? (
              <span className={cn(OVERLINE, 'flex items-center gap-2')}>
                Wersja {String.fromCharCode(65 + index)}
                {row.variants[index] ? (
                  <VariantScore score={row.variants[index]} />
                ) : null}
              </span>
            ) : null}
            <p>
              <span className="font-semibold">Ruch zawodnika.</span> {wording.played}
            </p>
            <p>
              <span className="font-semibold">Alternatywa.</span> {wording.alternative}
            </p>
          </div>
        ))}

        <Separator />

        <div className="flex flex-col gap-3">
          <span className={OVERLINE}>Komentarze recenzentów</span>
          {row.responses.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nikt jeszcze tego nie ocenił.</p>
          ) : (
            row.responses.map((response) => (
              <div
                key={response.nick}
                className="flex flex-col gap-1.5 rounded-lg bg-muted p-3"
              >
                <span className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-semibold">{response.nick}</span>
                  {response.answer.accuracy ? (
                    <Badge
                      tone={
                        response.answer.accuracy === 'trafny'
                          ? 'green'
                          : response.answer.accuracy === 'czesciowo'
                            ? 'amber'
                            : 'clay'
                      }
                    >
                      {response.answer.accuracy}
                    </Badge>
                  ) : null}
                  {response.answer.better ? (
                    <Badge tone="navy">
                      wybór: {preferredWording(row, response.answer.better)}
                    </Badge>
                  ) : null}
                  {response.answer.altNatural === 'sztuczny' ||
                  response.answer.altNatural === 'nie' ? (
                    <Badge tone="clay">sztuczna alternatywa</Badge>
                  ) : null}
                  <PolePlacementBadge value={response.answer.polePlacement} />
                </span>
                {response.answer.comment ? (
                  <p className="text-sm leading-relaxed text-ink-soft">
                    {response.answer.comment}
                  </p>
                ) : null}
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  )
}
